import { useMemo, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import LoadingContainer from 'components/common/LoadingContainer';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { useCaseStudies, useDeleteCaseStudy } from 'hooks/useCaseStudies';
import CaseStudyCard from './CaseStudyCard';
import CaseStudyFormModal from './CaseStudyFormModal';

// WeConnect > Case Study: impact and feedback stories across all children.
const CaseStudiesPage = () => {
  const { caseStudies, isLoading } = useCaseStudies();
  const { deleteCaseStudy, isDeleting } = useDeleteCaseStudy();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return caseStudies;
    return caseStudies.filter(cs =>
      [
        cs.title,
        cs.life_before,
        cs.how_found_out,
        cs.experience,
        cs.life_now,
        cs.quote,
        cs.orphan?.first_name,
        cs.orphan?.last_name,
        cs.branch?.name
      ]
        .filter(Boolean)
        .some(v => v.toLowerCase().includes(term))
    );
  }, [caseStudies, search]);

  if (isLoading) return <LoadingContainer />;

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Flex
            justifyContent="between"
            alignItems="center"
            className="flex-wrap gap-2"
          >
            <div>
              <h5 className="mb-1">
                <FontAwesomeIcon icon="book-open" className="me-2" />
                Case Studies
              </h5>
              <p className="text-600 fs--1 mb-0">
                Beneficiary impact and feedback reports · {caseStudies.length}{' '}
                total
              </p>
            </div>
            <Row className="g-2 align-items-center">
              <Col xs="auto">
                <Form.Control
                  size="sm"
                  placeholder="Search by child, title or story..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ minWidth: 240 }}
                />
              </Col>
              <Col xs="auto">
                <IconButton
                  icon="plus"
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setEditing(null);
                    setShowForm(true);
                  }}
                >
                  New Case Study
                </IconButton>
              </Col>
            </Row>
          </Flex>
        </Card.Body>
      </Card>

      {filtered.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5 text-600">
            {caseStudies.length === 0
              ? 'No case studies yet. Add the first one to start building the impact record.'
              : 'No case studies match your search.'}
          </Card.Body>
        </Card>
      ) : (
        filtered.map(cs => (
          <CaseStudyCard
            key={cs.id}
            caseStudy={cs}
            onEdit={c => {
              setEditing(c);
              setShowForm(true);
            }}
            onDelete={setToDelete}
          />
        ))
      )}

      {showForm && (
        <CaseStudyFormModal
          show={showForm}
          onHide={() => setShowForm(false)}
          caseStudy={editing}
        />
      )}
      <ConfirmationModal
        show={!!toDelete}
        onHide={() => setToDelete(null)}
        loading={isDeleting}
        title="Delete case study"
        confirmText="Delete"
        handleConfirm={() =>
          deleteCaseStudy(toDelete.id, { onSuccess: () => setToDelete(null) })
        }
        body={
          toDelete ? `Delete "${toDelete.title}"? This cannot be undone.` : ''
        }
      />
    </>
  );
};

export default CaseStudiesPage;
